/**
 * PostingModal - Enhanced modal for creating posts after cooking sessions
 * 
 * Features:
 * - Title input (required)
 * - Final dish photo upload (webcam or file)
 * - Progress photos selection (checkboxes)
 * - Submit to create post
 */

import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';
import photoCapture from '../services/photoCapture';

const PostingModal = ({
    isOpen,
    onClose,
    onSubmit,
    sessionId,
    sessionReport,
    initialTitle = ''
}) => {
    const [title, setTitle] = useState(initialTitle);
    const [finalDishPhoto, setFinalDishPhoto] = useState(null);
    const [finalDishPhotoPreview, setFinalDishPhotoPreview] = useState(null);
    const [selectedProgressPhotos, setSelectedProgressPhotos] = useState(new Set());
    const [progressPhotos, setProgressPhotos] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Extract progress photos from session report
    useEffect(() => {
        if (sessionReport?.photos) {
            const photos = sessionReport.photos.map(photo => ({
                id: photo.id,
                timestamp: photo.timestamp,
                photoData: photo.imageData, // This is the base64 data
                stepIndex: photo.stepIndex,
                notes: photo.reminderMessage
            }));
            setProgressPhotos(photos);
        }
    }, [sessionReport]);

    const handleFinalPhotoCapture = async () => {
        try {
            const blob = await photoCapture.capturePhotoWithUI();
            const compressedBlob = await photoCapture.compressImage(blob, 1024, 0.8);
            const base64 = await photoCapture.blobToBase64(compressedBlob);
            
            setFinalDishPhoto(base64);
            setFinalDishPhotoPreview(URL.createObjectURL(compressedBlob));
            setError('');
        } catch (error) {
            console.error('Error capturing final dish photo:', error);
            if (!error.message.includes('cancelled')) {
                setError('Failed to capture photo. Please try again.');
            }
        }
    };

    const handleRemoveFinalPhoto = () => {
        setFinalDishPhoto(null);
        if (finalDishPhotoPreview) {
            URL.revokeObjectURL(finalDishPhotoPreview);
        }
        setFinalDishPhotoPreview(null);
    };

    const toggleProgressPhoto = (photoId) => {
        setSelectedProgressPhotos(prev => {
            const newSet = new Set(prev);
            if (newSet.has(photoId)) {
                newSet.delete(photoId);
            } else {
                newSet.add(photoId);
            }
            return newSet;
        });
    };

    const handleSubmit = async () => {
        if (!title.trim()) {
            setError('Please enter a title for your dish');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const postData = {
                title: title.trim(),
                finalDishPhoto,
                progressPhotos: Array.from(selectedProgressPhotos),
                sessionId
            };

            await onSubmit(postData);
            onClose();
        } catch (error) {
            console.error('Error creating post:', error);
            setError(error.message || 'Failed to create post. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (finalDishPhotoPreview) {
            URL.revokeObjectURL(finalDishPhotoPreview);
        }
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} maxWidth="3xl">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>
                            Share Your Creation
                        </Heading>
                        <Text className="text-neutral-900 opacity-60 mt-1">
                            Add a title and photos to create your post
                        </Text>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                        disabled={isSubmitting}
                    >
                        ✕
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                        <Text className="text-sm text-red-900">{error}</Text>
                    </div>
                )}

                <div className="space-y-6">
                    {/* Title Input */}
                    <div>
                        <Text className="text-sm font-medium mb-2 block text-neutral-900">
                            Dish Title *
                        </Text>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Perfect Homemade Pizza"
                            className="w-full p-3 border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent"
                            maxLength={100}
                            disabled={isSubmitting}
                        />
                        <Text className="text-xs opacity-60 mt-1 text-neutral-900">
                            {title.length}/100 characters
                        </Text>
                    </div>

                    {/* Final Dish Photo */}
                    <div>
                        <Text className="text-sm font-medium mb-2 block text-neutral-900">
                            Final Dish Photo
                        </Text>
                        {finalDishPhoto ? (
                            <div className="relative">
                                <img
                                    src={finalDishPhotoPreview}
                                    alt="Final dish"
                                    className="w-full h-64 object-cover border border-neutral-300"
                                />
                                <button
                                    onClick={handleRemoveFinalPhoto}
                                    disabled={isSubmitting}
                                    className="absolute top-2 right-2 px-3 py-1 bg-red-600 text-white text-sm font-mono uppercase tracking-wider hover:opacity-60 disabled:opacity-50"
                                >
                                    Remove
                                </button>
                            </div>
                        ) : (
                            <div className="border border-neutral-300 p-6 text-center bg-neutral-50">
                                <Text className="text-neutral-900 opacity-60 mb-4">
                                    Capture or upload a photo of your final dish
                                </Text>
                                <button
                                    onClick={handleFinalPhotoCapture}
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 disabled:opacity-50"
                                >
                                    📸 Add Photo
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Progress Photos */}
                    {progressPhotos.length > 0 && (
                        <div>
                            <Text className="text-sm font-medium mb-2 block text-neutral-900">
                                Progress Photos ({selectedProgressPhotos.size} selected)
                            </Text>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto border border-neutral-300 p-3 bg-neutral-50">
                                {progressPhotos.map((photo) => (
                                    <div
                                        key={photo.id}
                                        className={`relative cursor-pointer border-2 ${
                                            selectedProgressPhotos.has(photo.id)
                                                ? 'border-neutral-900'
                                                : 'border-transparent'
                                        } hover:border-neutral-600 transition-colors`}
                                        onClick={() => toggleProgressPhoto(photo.id)}
                                    >
                                        <img
                                            src={photo.photoData}
                                            alt={`Progress at step ${photo.stepIndex + 1}`}
                                            className="w-full h-24 object-cover"
                                        />
                                        {selectedProgressPhotos.has(photo.id) && (
                                            <div className="absolute top-1 right-1 w-6 h-6 bg-neutral-900 text-neutral-0 flex items-center justify-center text-xs font-mono">
                                                ✓
                                            </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 font-mono">
                                            Step {photo.stepIndex + 1}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Submit Buttons */}
                    <div className="flex gap-3 pt-4 border-t border-neutral-200">
                        <button
                            onClick={handleSubmit}
                            disabled={!title.trim() || isSubmitting}
                            className="flex-1 px-4 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 disabled:opacity-50 transition-opacity"
                        >
                            {isSubmitting ? 'Creating Post...' : 'Create Post'}
                        </button>
                        <button
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="px-4 py-3 bg-neutral-50 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PostingModal;


